import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ApiNotificationService } from '@rcs/notify';
import { LmHttpAdapter } from './lm-http-adapter';
import {
  LeaveRequest, LeaveBalance, Holiday, HourlyRequest, LeaveType,
  CreateLeaveRequestDto, CreateHourlyRequestDto, CreateCompOffRequestDto,
  ProcessLeaveRequestDto, ProcessLeaveRequestBackendDto,
  ProcessOptionalHolidayRequestDto, SaveHolidayInput,
  Country, State, Supervisor, WorkedDate, Manager,
  CancelLeaveRequestDto, SaveLeaveTypeInput, LeaveTypeDetail,
  LeaveAllocationItem, ProcessCompOffDto, CompOffRequest, TakenCompOff,
  PolicyDocumentRow, GiftLeaveDto, AvailGiftLeaveDto, GiftedLeaveBalance,
  ManagerUpdateNotification, ManagerUpdatesResponse,
  SaveLeaveBalanceRemarkDto, SaveLeaveBalanceRemarkResult,
  LeaveLedgerEntry,
  SimulateAccrualInput, AccrualSimulationRow, TenureTier,
} from '../models/leave.model';
import { parseLocalDate } from '../utils/date.utils';


@Injectable({ providedIn: 'root' })
export class LeaveService {
  private readonly adapter = inject(LmHttpAdapter);
  private readonly notify  = inject(ApiNotificationService);

  private isTechnicalMessage(value: string): boolean {
    const text = String(value || '').toLowerCase();
    return (
      text.includes('http failure response for') ||
      text.includes('xmlhttprequest') ||
      text.includes('exception') ||
      text.includes('stack trace') ||
      text.includes(' at ') ||
      text.includes('http://') ||
      text.includes('https://') ||
      text.includes('subquery') ||
      text.includes('conversion failed') ||
      text.includes('violation of') ||
      text.includes('deadlock') ||
      text.includes('transaction')
    );
  }

  private statusMessage(status: number | undefined): string {
    switch (status) {
      case 0:
        return 'Unable to connect to server. Please check your connection.';
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'Requested resource was not found.';
      default:
        return status && status >= 500
          ? 'Server error. Please try again later.'
          : 'An unexpected error occurred. Please try again.';
    }
  }

  /** Extracts a human-readable message from an HTTP error response. */
  private extractMessage(err: any): string {
    const rawMessage =
      err?.error?.message ||
      err?.error?.error   ||
      err?.message ||
      '';

    if (rawMessage && !this.isTechnicalMessage(rawMessage)) {
      return rawMessage;
    }

    return this.statusMessage(err?.status);
  }

  /**
   * For READ operations — shows error snackbar and returns a safe empty fallback
   * so the UI stays functional.
   */
  private handleError<T>(fallback: T) {
    return (err: any): Observable<T> => {
      this.notify.addError(this.extractMessage(err));
      return of(fallback);
    };
  }

  /**
   * For MUTATION operations — shows error snackbar and rethrows so the
   * calling component can react (e.g. keep the form open, reset a spinner).
   */
  private handleMutationError() {
    return (err: any): Observable<never> => {
      this.notify.addError(this.extractMessage(err));
      return throwError(() => err);
    };
  }

  // ─── Leave Requests ──────────────────────────────────────────────────────

  getMyLeaveRequests(): Observable<LeaveRequest[]> {
    return this.adapter.get<LeaveRequest[]>('lmGetMyLeaveRequests').pipe(
      catchError(this.handleError<LeaveRequest[]>([])),
    );
  }

  getMyHourlyRequests(): Observable<HourlyRequest[]> {
    return this.adapter.get<LeaveRequest[]>('lmGetMyLeaveRequests').pipe(
      map((rows) =>
        (rows ?? [])
          .filter((r) => !!r?.isHourlyLeaveRequest)
          .map((r) => ({
            id: r.id,
            userId: r.userId,
            userName: r.userName,
            requestDate: r.startDate,
            fromTime: this.extractTimeString(r.startDate),
            toTime: this.extractTimeString(r.endDate),
            reason: r.reason ?? '',
            status: r.status,
            approvedBy: r.approvedBy,
            approvedOn: r.approvedOn,
            rejectionReason: r.rejectionReason,
            createdOn: r.createdOn,
            tenantId: r.tenantId,
          } as HourlyRequest)),
      ),
      catchError(this.handleError<HourlyRequest[]>([])),
    );
  }

  getTeamLeaveRequests(query?: { fromDate?: string; toDate?: string }): Observable<LeaveRequest[]> {
    const queryParams: Record<string, string> = {};
    if (query?.fromDate) queryParams['fromDate'] = query.fromDate;
    if (query?.toDate)   queryParams['toDate']   = query.toDate;
    return this.adapter.getFiltered<LeaveRequest[]>('lmGetTeamLeaveRequests', queryParams).pipe(
      catchError(this.handleError<LeaveRequest[]>([])),
    );
  }

  getLeaveTypes(): Observable<LeaveType[]> {
    return this.adapter.get<any[]>('lmGetLeaveTypes').pipe(
      map(rows => (rows ?? []).map((r: any) => ({
        id:        r.ID              ?? r.id,
        name:      r.LEAVE_TYPE_NAME ?? r.leaveTypeName ?? r.name ?? '',
        shortCode: r.LEAVE_TYPE_CODE ?? r.leaveTypeCode ?? r.shortCode ?? '',
        isActive:  (r.IS_ACTIVE === undefined || r.IS_ACTIVE === null)
                     ? true
                     : (r.IS_ACTIVE === '1' || r.IS_ACTIVE === 1 || r.IS_ACTIVE === true),
        tenantId:  r.TENANT_ID ?? r.tenantId ?? '',
      } as LeaveType))),
      catchError(this.handleError<LeaveType[]>([])),
    );
  }

  getLeaveRequestById(id: string): Observable<LeaveRequest> {
    return this.adapter.get<LeaveRequest>('lmGetLeaveRequestById', [{ id }]).pipe(
      catchError(this.handleError<LeaveRequest>(null!)),
    );
  }

  createLeaveRequest(dto: CreateLeaveRequestDto): Observable<{ success: boolean; message: string; id: string }> {
    return this.adapter.post<CreateLeaveRequestDto, { success: boolean; message: string; id: string }>(
      'lmCreateLeaveRequest', 'LeaveRequestSaveInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  updateLeaveRequest(dto: CreateLeaveRequestDto): Observable<{ success: boolean; message: string }> {
    return this.adapter.put<CreateLeaveRequestDto, { success: boolean; message: string }>(
      'lmUpdateLeaveRequest', 'LeaveRequestSaveInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  processLeaveRequest(dto: ProcessLeaveRequestDto): Observable<{ success: boolean; message: string }> {
    const backendDto: ProcessLeaveRequestBackendDto = {
      employeeLeaveRequestId: dto.leaveId,
      action: dto.action.toUpperCase() as 'APPROVE' | 'REJECT',
      rejectionReason: dto.rejectionReason,
    };
    return this.adapter.post<ProcessLeaveRequestBackendDto, { success: boolean; message: string }>(
      'lmProcessLeaveRequest', 'LeaveRequestProcessInput', backendDto
    ).pipe(catchError(this.handleMutationError()));
  }

  createHourlyRequest(dto: CreateHourlyRequestDto): Observable<{ success: boolean; message: string; id: string; error?: string }> {
    // Embed fromTime/toTime into local datetime strings so DB receives explicit
    // time components for hourly requests.
    // Format: "YYYY-MM-DDTHH:mm:ss.SSS" (no Z — keep local wall-clock time)
    const leaveStartDate = dto.fromTime
      ? `${dto.requestDate}T${dto.fromTime}:00.000`
      : dto.requestDate;
    const leaveEndDate = dto.toTime
      ? `${dto.requestDate}T${dto.toTime}:00.000`
      : dto.requestDate;

    return this.adapter.post<CreateHourlyRequestDto, { success: boolean; message: string; id: string; error?: string }>(
      'lmCreateLeaveRequest', 'LeaveRequestSaveInput', {
        ...dto,
        leaveStartDate,
        leaveEndDate,
        isHourlyLeaveRequest: true,
        requestedToEmployeeId: dto.requestedToEmployeeIds?.[0],
      } as any
    ).pipe(catchError(this.handleMutationError()));
  }

  // ─── Leave Balance ────────────────────────────────────────────────────────

  private toNumber(value: unknown, fallback = 0): number {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  private extractTimeString(value: string | Date | null | undefined): string {
    if (!value) {
      return '-';
    }
    const d = parseLocalDate(value);
    if (Number.isNaN(d.getTime())) {
      return '-';
    }
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  private mapLeaveBalanceRow(row: any): LeaveBalance {
    const remainingBalance = this.toNumber(
      row?.REMAINING_BALANCE ?? row?.remainingBalance,
      0,
    );
    const reservedLeaves = this.toNumber(
      row?.RESERVED_LEAVES ?? row?.reservedLeaves,
      0,
    );

    const availableRaw = row?.AVAILABLE_BALANCE ?? row?.availableBalance;
    const availableBalance =
      availableRaw === null || availableRaw === undefined || availableRaw === ''
        ? remainingBalance - reservedLeaves
        : this.toNumber(availableRaw, remainingBalance - reservedLeaves);

    return {
      leaveTypeId: row?.LEAVE_TYPE_ID ?? row?.leaveTypeId ?? '',
      leaveTypeName: row?.LEAVE_TYPE_NAME ?? row?.leaveTypeName ?? '',
      leavePeriodStartDate:
        row?.LEAVE_PERIOD_START_DATE ?? row?.leavePeriodStartDate ?? null,
      carryForwardValidityInMonths:
        row?.CARRY_FORWARD_VALIDITY_IN_MONTHS ??
        row?.carryForwardValidityInMonths ??
        null,
      totalAllocated: this.toNumber(
        row?.TOTAL_ALLOCATED ?? row?.totalAllocated,
        0,
      ),
      totalUsed: this.toNumber(row?.TOTAL_USED ?? row?.totalUsed, 0),
      carriedForwardLeaves: this.toNumber(
        row?.CARRIED_FORWARD_LEAVES ?? row?.carriedForwardLeaves,
        0,
      ),
      remainingBalance,
      reservedLeaves,
      availableBalance,
      validTill: row?.VALID_TILL ?? row?.validTill ?? null,
      isCarriedForward:
        row?.IS_CARRIED_FORWARD === '1' ||
        row?.IS_CARRIED_FORWARD === 1 ||
        row?.IS_CARRIED_FORWARD === true ||
        row?.isCarriedForward === true,
      unpaidLeaves: this.toNumber(
        row?.UNPAID_LEAVES ?? row?.unpaidLeaves,
        0,
      ),
      employeeId: row?.EMPLOYEE_ID ?? row?.employeeId ?? null,
      employeeName: row?.EMPLOYEE_NAME ?? row?.employeeName ?? null,
      employeeNumber: row?.EMPLOYEE_NUMBER ?? row?.employeeNumber ?? null,
      department: row?.DEPARTMENT ?? row?.department ?? null,
    };
  }

  private mapLeaveBalanceRows(rows: any[] | null | undefined): LeaveBalance[] {
    return (rows ?? []).map((row: any) => this.mapLeaveBalanceRow(row));
  }

  getMyLeaveBalance(): Observable<LeaveBalance[]> {
    return this.adapter.get<any[]>('lmGetMyLeaveBalance').pipe(
      map(rows => this.mapLeaveBalanceRows(rows)),
      catchError(this.handleError<LeaveBalance[]>([])),
    );
  }

  private mapLedgerRow(r: any): LeaveLedgerEntry {
    return {
      id:              r?.ID               ?? r?.id              ?? '',
      transactionDate: r?.TRANSACTION_DATE ?? r?.transactionDate ?? null,
      transactionType: r?.TRANSACTION_TYPE ?? r?.transactionType ?? r?.DATA_VALUE ?? '',
      leaveTypeId:     r?.LEAVE_TYPE_ID    ?? r?.leaveTypeId     ?? '',
      leaveTypeName:   r?.LEAVE_TYPE_NAME  ?? r?.leaveTypeName   ?? '',
      quantity:        this.toNumber(r?.QUANTITY        ?? r?.quantity, 0),
      runningBalance:  this.toNumber(r?.RUNNING_BALANCE ?? r?.runningBalance, 0),
      reference:       r?.REFERENCE        ?? r?.reference       ?? null,
    };
  }

  /** The current employee's full ledger timeline (every balance movement). */
  getMyLeaveLedger(): Observable<LeaveLedgerEntry[]> {
    return this.adapter.get<any[]>('lmGetLeaveLedger').pipe(
      map(rows => (rows ?? []).map(r => this.mapLedgerRow(r))),
      catchError(this.handleError<LeaveLedgerEntry[]>([])),
    );
  }

  // ─── Holidays ─────────────────────────────────────────────────────────────

  getHolidays(): Observable<Holiday[]> {
    // createHoliday/updateHoliday always write isOptional as a camelCase
    // boolean, but this read side previously passed the raw response through
    // untouched — if the backend's SELECT returns it under a different
    // name/casing (e.g. IS_OPTIONAL) or as a string/number rather than a real
    // boolean, `item.isOptional` reads as undefined/falsy and every holiday
    // shows as "Mandatory" regardless of what was saved. Normalize it the same
    // way getMyOptionalHolidays already does for its own fields.
    return this.adapter.get<any[]>('lmGetHolidays').pipe(
      map(arr => (arr ?? []).map(item => {
        const rawIsOptional = item.isOptional
          ?? item.IsOptional
          ?? item.IS_OPTIONAL
          ?? item.isOptionalHoliday
          ?? item.IsOptionalHoliday
          ?? item.IS_OPTIONAL_HOLIDAY
          ?? item.optionalHoliday
          ?? item.OptionalHoliday
          ?? item.OPTIONAL_HOLIDAY;

        return {
          ...item,
          holidayName: item.holidayName ?? item.HolidayName ?? item.HOLIDAY_NAME,
          holidayDate: item.holidayDate ?? item.HolidayDate ?? item.HOLIDAY_DATE,
          holidayType: item.holidayType ?? item.HolidayType ?? item.HOLIDAY_TYPE,
          isOptional: this._toBoolean(rawIsOptional),
          applicableToCountryId: item.applicableToCountryId ?? item.ApplicableToCountryId ?? item.APPLICABLE_TO_COUNTRY_ID ?? null,
          applicableToStateId: item.applicableToStateId ?? item.ApplicableToStateId ?? item.APPLICABLE_TO_STATE_ID ?? null,
          notes: item.notes ?? item.Notes ?? item.NOTES ?? null,
        } as Holiday;
      })),
      catchError(this.handleError<Holiday[]>([])),
    );
  }

  private _toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }
    return false;
  }

  createHoliday(input: SaveHolidayInput): Observable<{ holiday: any; error: any }> {
    return this.adapter.post<SaveHolidayInput, { holiday: any; error: any }>(
      'lmCreateHoliday', 'SaveHolidayInput', input
    ).pipe(catchError(this.handleMutationError()));
  }

  updateHoliday(input: SaveHolidayInput): Observable<{ holiday: any; error: any }> {
    return this.adapter.put<SaveHolidayInput, { holiday: any; error: any }>(
      'lmUpdateHoliday', 'SaveHolidayInput', input
    ).pipe(catchError(this.handleMutationError()));
  }

  deleteHoliday(id: string): Observable<{ success: boolean }> {
    return this.adapter.delete<{ success: boolean }>('lmDeleteHoliday', [{ id }]).pipe(
      catchError(this.handleMutationError()),
    );
  }

  // ─── Optional Holidays ────────────────────────────────────────────────────

  getMyOptionalHolidays(): Observable<any[]> {
    // normalize fields so consumers can rely on camelCase names
    return this.adapter.get<any[]>('lmGetMyOptionalHolidays').pipe(
      map(arr => (arr ?? []).map(item => ({
        // Spread the raw item FIRST so the normalized fields below always win.
        // Spreading it last (as this used to) let a raw camelCase key that
        // exists but is null/undefined — e.g. the backend sending both STATUS
        // and a blank status — silently clobber the just-computed fallback,
        // blanking every cell in the grid right after an update re-fetched
        // this list. Other fields still pass through unchanged.
        ...item,
        id: item.ID ?? item.id,
        userId: item.EMPLOYEE_ID ?? item.userId,
        employeeNumber: item.EMPLOYEE_NUMBER ?? item.employeeNumber,
        employeeName: item.EMPLOYEE_NAME ?? item.employeeName,
        holidayId: item.HOLIDAY_ID ?? item.holidayId,
        holidayName: item.HOLIDAY_NAME ?? item.holidayName,
        holidayCode: item.HOLIDAY_CODE ?? item.holidayCode,
        holidayDate: item.HOLIDAY_DATE ?? item.holidayDate,
        requestSubmittedDate: item.REQUEST_SUBMITTED_DATE ?? item.requestSubmittedDate,
        status: item.STATUS ?? item.status,
      }))),
      catchError(this.handleError<any[]>([])),
    );
  }

  requestOptionalHoliday(holidayId: string): Observable<{ request: any; error: any }> {
    return this.adapter.post<{ holidayId: string }, { request: any; error: any }>(
      'lmRequestOptionalHoliday', 'SaveOptionalHolidayRequestInput', { holidayId }
    ).pipe(catchError(this.handleMutationError()));
  }

  processOptionalHoliday(dto: ProcessOptionalHolidayRequestDto): Observable<{ id: string; message: string }> {
    // Backend expects title-case: 'Approve' | 'Reject'
    const payload = {
      requestId: dto.requestId,
      action: dto.action as 'Approve' | 'Reject',
      rejectionReason: dto.rejectionReason ?? null,
    };
    return this.adapter.post<typeof payload, { id: string; message: string }>(
      'lmProcessOptionalHoliday', 'ProcessOptionalHolidayRequestInput', payload
    ).pipe(catchError(this.handleMutationError()));
  }

  // ─── Master Data ──────────────────────────────────────────────────────────

  getCountries(): Observable<Country[]> {
    return this.adapter.get<any[]>('lmGetCountries').pipe(
      map(arr => (arr ?? []).map((c: any) => ({
        id:      c.id      ?? c.ID,
        country: c.country ?? c.countryName ?? c.COUNTRY ?? c.COUNTRY_NAME ?? c.name ?? '',
      } as Country))),
      catchError(this.handleError<Country[]>([])),
    );
  }

  getAllStates(): Observable<State[]> {
    return this.adapter.get<State[]>('lmGetAllStates').pipe(
      catchError(this.handleError<State[]>([])),
    );
  }

  getStatesByCountry(countryId: string): Observable<State[]> {
    return this.adapter.get<State[]>('lmGetStatesByCountry', [{ countryId }]).pipe(
      catchError(this.handleError<State[]>([])),
    );
  }

  getSupervisors(): Observable<Supervisor[]> {
    return this.adapter.get<Supervisor[]>('lmGetSupervisors').pipe(
      catchError(this.handleError<Supervisor[]>([])),
    );
  }

  getEmployeesBySupervisorEmployeeId(supervisorEmployeeId: string): Observable<any[]> {
    return this.adapter.get<any[]>('fetchEmployeesBySupervisorEmpId', [{ supervisorEmployeeId }]).pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

  // ─── Comp Off ─────────────────────────────────────────────────────────────

  createCompOffRequest(dto: CreateCompOffRequestDto): Observable<{ success: boolean; message: string; id: string }> {
    return this.adapter.post<CreateCompOffRequestDto, { success: boolean; message: string; id: string }>(
      'lmCreateCompOff', 'SaveCompOffInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  // ─── Cancel Leave Request ─────────────────────────────────────────────────

  cancelLeaveRequest(id: string): Observable<{ success: boolean; message: string }> {
    return this.adapter.post<CancelLeaveRequestDto, { success: boolean; message: string; error?: string }>(
      'lmCancelLeaveRequest', 'CancelLeaveRequestInput', { id }
    ).pipe(
      switchMap((res) =>
        res?.success === false
          ? throwError(() => new Error(res.error || 'Unable to cancel the leave request.'))
          : of(res)
      ),
      tap((res) => this.notify.addSuccess(res?.message || 'Leave request cancelled.')),
      catchError(this.handleMutationError()),
    );
  }

  // ─── Manager: Pending Leave Requests ─────────────────────────────────────

  getPendingRequestsForManager(): Observable<LeaveRequest[]> {
    return this.adapter.get<LeaveRequest[]>('lmGetPendingManagerRequests').pipe(
      catchError(this.handleError<LeaveRequest[]>([])),
    );
  }

  getManagerUpdates(): Observable<ManagerUpdateNotification[]> {
    return this.adapter.get<ManagerUpdatesResponse>('lmGetManagerUpdates').pipe(
      map((response) => response?.data ?? []),
      catchError(this.handleError<ManagerUpdateNotification[]>([])),
    );
  }

  // ─── HR: All Leave Requests ───────────────────────────────────────────────

  getAllLeaveRequestsForHR(query?: { status?: string; fromDate?: string; toDate?: string }): Observable<LeaveRequest[]> {
    const queryParams: Record<string, string> = {};
    if (query?.fromDate) queryParams['fromDate'] = query.fromDate;
    if (query?.toDate)   queryParams['toDate']   = query.toDate;
    if (query?.status)   queryParams['status']   = query.status;
    return this.adapter.getFiltered<LeaveRequest[]>('lmGetAllLeaveRequestsHR', queryParams).pipe(
      catchError(this.handleError<LeaveRequest[]>([])),
    );
  }

  // ─── Employee Leave Balance (by employeeId) ───────────────────────────────

  getEmployeeLeaveBalance(employeeId: string): Observable<LeaveBalance[]> {
    return this.adapter.get<any[]>('lmGetEmployeeLeaveBalance', [{ employeeId }]).pipe(
      map(rows => this.mapLeaveBalanceRows(rows)),
      catchError(this.handleError<LeaveBalance[]>([])),
    );
  }

  /**
   * HR dashboard: all employees' leave balances, pivoted to one row per employee.
   * month is 1-based (1=Jan…12=Dec).
   */
  getTenantLeaveBalanceSummary(month: number, year: number, filterByManagerId?: string): Observable<any[]> {
    const queryParams: Record<string, string> = {
      month: String(month),
      year: String(year),
    };
    if (filterByManagerId) {
      queryParams['filterByManagerId'] = filterByManagerId;
    }
    return this.adapter.getFiltered<any[]>('lmGetTenantLeaveBalance', queryParams).pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

  saveEmployeeLeaveBalanceRemark(dto: SaveLeaveBalanceRemarkDto): Observable<SaveLeaveBalanceRemarkResult> {
    return this.adapter.post<SaveLeaveBalanceRemarkDto, SaveLeaveBalanceRemarkResult>(
      'lmSaveEmployeeLeaveBalanceRemark',
      'SaveLeaveBalanceNotesInput',
      dto,
    ).pipe(catchError(this.handleMutationError()));
  }

  // ─── Comp Off (real endpoints) ────────────────────────────────────────────

  getMyCompOffRequests(): Observable<CompOffRequest[]> {
    return this.adapter.get<CompOffRequest[]>('lmGetMyCompOffRequests').pipe(
      catchError(this.handleError<CompOffRequest[]>([])),
    );
  }

  getEligibleWorkingDatesForCompOff(): Observable<{ date: string; dayName: string; holidayName: string }[]> {
    return this.adapter.get<any[]>('lmGetEligibleWorkingDatesForCompOff').pipe(
      map(rows => (rows ?? [])
        .map((r: any) => {
          const raw = r.WORKED_DATES ?? r.workedDates ?? r.workedDate ?? r.WORKING_DATE ?? r.workingDate ?? r.WorkingDate ?? r.date ?? r.Date ?? null;
          if (!raw) return null;
          // Parse as local date — SP stores local midnight as UTC, so split('T')[0]
          // gives the wrong (UTC) date in UTC+ timezones.
          const d = parseLocalDate(raw);
          const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
          return {
            date,
            dayName: r.DAY_NAME ?? r.dayName ?? '',
            holidayName: r.HOLIDAY_NAME ?? r.holidayName ?? '',
          };
        })
        .filter((d): d is { date: string; dayName: string; holidayName: string } => !!d),
      ),
      catchError(this.handleError<{ date: string; dayName: string; holidayName: string }[]>([])),
    );
  }

  getEmployeeCompOffRequests(employeeId: string): Observable<any[]> {
    return this.adapter.get<any[]>('lmGetEmployeeCompOffRequests', [{ employeeId }]).pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

  getEmployeeOptionalHolidays(employeeId: string): Observable<any[]> {
    return this.adapter.get<any[]>('lmGetEmployeeOptionalHolidays', [{ employeeId }]).pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

  getAllCompOffRequestsForHR(): Observable<CompOffRequest[]> {
    return this.adapter.get<CompOffRequest[]>('lmGetAllCompOffRequests').pipe(
      catchError(this.handleError<CompOffRequest[]>([])),
    );
  }

  processCompOffRequest(dto: ProcessCompOffDto): Observable<{ success: boolean; message: string }> {
    return this.adapter.post<ProcessCompOffDto, { success: boolean; message: string }>(
      'lmProcessCompOff', 'ProcessCompOffInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  // ─── Leave Types Management ───────────────────────────────────────────────

  getAllLeaveTypesManagement(): Observable<LeaveTypeDetail[]> {
    return this.adapter.get<any[]>('lmGetAllLeaveTypesMgmt').pipe(
      map(rows => (rows ?? []).map((r: any) => {
        const qty = parseFloat(r.QUANTITY_ALLOCATED_FOR_EACH_PERIOD ?? r.quantityAllocatedForEachPeriod ?? '0');
        const pct = r.PERCENTAGE_CARRIED_FORWARD != null
          ? parseFloat(r.PERCENTAGE_CARRIED_FORWARD)
          : (r.percentageCarriedForward ?? null);
        const isCarried = (() => { const v = r.IS_CARRIED_FORWARD ?? r.isCarriedForward; return v === '1' || v === 1 || v === true; })();

        const start = r.LEAVE_PERIOD_START_DATE ?? r.leavePeriodStartDate ?? null;
        const months = r.NUMBER_OF_MONTHS_VALID_TO != null
          ? parseInt(r.NUMBER_OF_MONTHS_VALID_TO, 10)
          : (r.numberOfMonthsValidTo ?? null);

        let fallbackValidTill: string | null = null;
        if (start && months != null) {
          const d = parseLocalDate(start);
          if (!isNaN(d.getTime())) {
            // validTill = last inclusive day of the valid period
            // e.g. start=2026-02-01, months=12 → add 12 months → 2027-02-01 → subtract 1 day → 2027-01-31
            d.setMonth(d.getMonth() + months);
            d.setDate(d.getDate() - 1);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            fallbackValidTill = `${y}-${m}-${day}`;
          }
        }

        return ({
        id:                                   r.ID                                       ?? r.id,
        leaveTypeName:                        r.LEAVE_TYPE_NAME                          ?? r.leaveTypeName,
        leaveTypeCode:                        r.LEAVE_TYPE_CODE                          ?? r.leaveTypeCode   ?? null,
        allocationFrequencyId:                r.ALLOCATION_FREQUENCY_ID                  ?? r.allocationFrequencyId,
        quantityAllocatedForEachPeriod:       qty,
        isApprovalRequired:                   (r.IS_APPROVAL_REQUIRED                    ?? r.isApprovalRequired)   === '1' || r.isApprovalRequired   === true,
        isCarriedForward:                     isCarried,
        carryForwardExpiryDate:               r.CARRY_FORWARD_EXPIRY_DATE                ?? r.carryForwardExpiryDate ?? null,
        percentageCarriedForward:             pct,
        isCountedInclusiveOfOtherHolidays:    (r.IS_COUNTED_INCLUSIVE_OF_OTHER_HOLIDAYS  ?? r.isCountedInclusiveOfOtherHolidays) === '1' || r.isCountedInclusiveOfOtherHolidays === true,
        isAllocatedAtStartOfPeriod:           (r.IS_ALLOCATED_AT_START_OF_PERIOD         ?? r.isAllocatedAtStartOfPeriod)        === '1' || r.isAllocatedAtStartOfPeriod        === true,
        maximumAllowedAccumulation:           r.MAXIMUM_ALLOWED_ACCUMULATION != null
                                                ? parseFloat(r.MAXIMUM_ALLOWED_ACCUMULATION)
                                                : (r.maximumAllowedAccumulation ?? null),
        maximumAllowedInOneInstance:          r.MAXIMUM_ALLOWED_IN_ONE_INSTANCE != null
                                                ? parseFloat(r.MAXIMUM_ALLOWED_IN_ONE_INSTANCE)
                                                : (r.maximumAllowedInOneInstance ?? null),
        isSuspendedDuringNoticePeriod:        (r.IS_SUSPENDED_DURING_NOTICE_PERIOD       ?? r.isSuspendedDuringNoticePeriod)     === '1' || r.isSuspendedDuringNoticePeriod     === true,
        isEncashable:                         (r.IS_ENCASHABLE                           ?? r.isEncashable)                      === '1' || r.isEncashable                      === true,
        isEncashableAboveMaximumAccumulation: (r.IS_ENCASHABLE_ABOVE_MAXIMUM_ACCUMULATION ?? r.isEncashableAboveMaximumAccumulation) === '1' || r.isEncashableAboveMaximumAccumulation === true,
        leavePeriodStartDate:                 start,
        numberOfMonthsValidTo:                months,
        isGiftingLeaveAllowed:                (r.IS_GIFTING_LEAVE_ALLOWED                ?? r.isGiftingLeaveAllowed)              === '1' || r.isGiftingLeaveAllowed              === true,
        maximumDaysAllowedToGift:             r.MAXIMUM_DAYS_ALLOWED_TO_GIFT != null
                                                ? parseFloat(r.MAXIMUM_DAYS_ALLOWED_TO_GIFT)
                                                : (r.maximumDaysAllowedToGift ?? null),
        isGiftReasonRequired:                 (r.IS_GIFT_REASON_REQUIRED                 ?? r.isGiftReasonRequired)               === '1' || r.isGiftReasonRequired               === true,
        isActive:                             !( (r.IS_ACTIVE ?? r.isActive) === '0' || (r.IS_ACTIVE ?? r.isActive) === 0 || (r.IS_ACTIVE ?? r.isActive) === false ),
        tenantId:                             r.TENANT_ID ?? r.tenantId,
        notes:                                r.NOTES ?? r.notes ?? null,
        carryForwardDays:                     r.carryForwardDays ?? (isCarried && pct != null ? Math.round((pct / 100) * qty) : null),
        validTill:                            r.validTill ?? fallbackValidTill,
        // PBI 34800 accrual-policy config
        accrualMethod:                        r.ACCRUAL_METHOD ?? r.accrualMethod ?? null,
        accrualRatePerHour:                   (r.ACCRUAL_RATE_PER_HOUR ?? r.accrualRatePerHour) != null
                                                ? Number(r.ACCRUAL_RATE_PER_HOUR ?? r.accrualRatePerHour) : null,
        prorateFirstPeriod:                   (() => { const v = r.PRORATE_FIRST_PERIOD ?? r.prorateFirstPeriod; return v == null ? null : (v === '1' || v === 1 || v === true); })(),
        prorationBasis:                       r.PRORATION_BASIS ?? r.prorationBasis ?? null,
        roundingMode:                         r.ROUNDING_MODE ?? r.roundingMode ?? null,
        roundingIncrement:                    (r.ROUNDING_INCREMENT ?? r.roundingIncrement) != null
                                                ? Number(r.ROUNDING_INCREMENT ?? r.roundingIncrement) : null,
      } as LeaveTypeDetail);
      })),
      catchError(this.handleError<LeaveTypeDetail[]>([])),
    );
  }

  createLeaveType(dto: SaveLeaveTypeInput): Observable<{ success: boolean; message: string }> {
    return this.adapter.post<SaveLeaveTypeInput, { success: boolean; message: string }>(
      'lmCreateLeaveType', 'SaveLeaveTypeInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  updateLeaveType(dto: SaveLeaveTypeInput): Observable<{ success: boolean; message: string }> {
    return this.adapter.put<SaveLeaveTypeInput, { success: boolean; message: string }>(
      'lmUpdateLeaveType', 'SaveLeaveTypeInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  deleteLeaveType(id: string): Observable<{ success: boolean }> {
    return this.adapter.delete<{ success: boolean }>('lmDeleteLeaveType', [{ id }]).pipe(
      catchError(this.handleMutationError()),
    );
  }

  /** PBI 34800: preview a policy's accrual for a sample employee (writes nothing). */
  simulateAccrual(dto: SimulateAccrualInput): Observable<AccrualSimulationRow[]> {
    return this.adapter.post<SimulateAccrualInput, AccrualSimulationRow[]>(
      'lmSimulateAccrual', 'SimulateAccrualInput', dto
    ).pipe(
      map(rows => (rows ?? []).map((r: any) => ({
        employeeId:    r.employeeId    ?? r.EMPLOYEE_ID    ?? null,
        hireDate:      r.hireDate      ?? r.HIRE_DATE      ?? null,
        periodIndex:   this.toNumber(r.periodIndex   ?? r.PERIOD_INDEX, 0),
        periodStart:   r.periodStart   ?? r.PERIOD_START   ?? null,
        periodEnd:     r.periodEnd     ?? r.PERIOD_END     ?? null,
        serviceMonths: (r.serviceMonths ?? r.SERVICE_MONTHS) != null
                         ? this.toNumber(r.serviceMonths ?? r.SERVICE_MONTHS, 0) : null,
        method:        r.method        ?? r.METHOD        ?? null,
        baseQty:       this.toNumber(r.baseQty      ?? r.BASE_QTY, 0),
        proratedQty:   this.toNumber(r.proratedQty  ?? r.PRORATED_QTY, 0),
        accruedQty:    this.toNumber(r.accruedQty   ?? r.ACCRUED_QTY, 0),
        runningTotal:  this.toNumber(r.runningTotal ?? r.RUNNING_TOTAL, 0),
      } as AccrualSimulationRow))),
      catchError(this.handleError<AccrualSimulationRow[]>([])),
    );
  }

  /** PBI 34800: a policy's active tenure tiers (for edit-mode round-trip). */
  getTenureTiers(leaveTypeId: string): Observable<TenureTier[]> {
    return this.adapter.get<any[]>('lmGetTenureTiers', [{ leaveTypeId }]).pipe(
      map(rows => (rows ?? []).map((r: any) => ({
        minMonthsOfService: this.toNumber(r.minMonthsOfService ?? r.MIN_MONTHS_OF_SERVICE, 0),
        quantityPerPeriod:  this.toNumber(r.quantityPerPeriod  ?? r.QUANTITY_PER_PERIOD, 0),
      } as TenureTier))),
      catchError(this.handleError<TenureTier[]>([])),
    );
  }

  // ─── Leave Allocations ────────────────────────────────────────────────────

  getAllLeaveAllocations(year: number): Observable<LeaveAllocationItem[]> {
    return this.adapter.get<LeaveAllocationItem[]>('lmGetLeaveAllocations', [{ year: String(year) }]).pipe(
      catchError(this.handleError<LeaveAllocationItem[]>([])),
    );
  }

  getEmployeeAllocations(employeeId: string, year: number): Observable<LeaveAllocationItem[]> {
    return this.adapter.get<LeaveAllocationItem[]>('lmGetEmployeeAllocations', [{ employeeId }, { year: String(year) }]).pipe(
      catchError(this.handleError<LeaveAllocationItem[]>([])),
    );
  }

  // ─── Policy Documents ─────────────────────────────────────────────────────

  getPolicyDocuments(): Observable<PolicyDocumentRow[]> {
    return this.adapter.get<PolicyDocumentRow[]>('lmGetPolicyDocuments').pipe(
      catchError(this.handleError<PolicyDocumentRow[]>([])),
    );
  }

  savePolicyDocument(dto: { documentTitle: string; documentUrl: string; leaveTypeId?: string | null }): Observable<{ success: boolean; id: string; message: string }> {
    const backendDto = {
      documents: [{
        documentTitle: dto.documentTitle,
        documentPath: dto.documentUrl,
        leaveTypeId: dto.leaveTypeId ?? null,
      }],
    };
    return this.adapter.post<typeof backendDto, { success: boolean; id: string; message: string }>(
      'lmSavePolicyDocument', 'SavePolicyDocumentInput', backendDto
    ).pipe(catchError(this.handleMutationError()));
  }

  deletePolicyDocument(id: string): Observable<{ success: boolean }> {
    return this.adapter.delete<{ success: boolean }>('lmDeletePolicyDocument', [{ id }]).pipe(
      catchError(this.handleMutationError()),
    );
  }

  /** Downloads a remote file through the NestJS proxy (avoids browser CORS). */
  downloadProxiedDocument(fileUrl: string): Observable<Blob> {
    return this.adapter.getBlob(
      'lmDownloadPolicyDocument',
      [{ url: encodeURIComponent(fileUrl) }],
    ).pipe(
      catchError(this.handleMutationError()),
    );
  }

  /** Gets the file size of a remote file via server-side HEAD (avoids CORS). */
  getPolicyDocumentFileInfo(fileUrl: string): Observable<{ contentLength: number | null; contentType: string }> {
    return this.adapter.getFileInfo(
      'lmPolicyDocumentFileInfo',
      [{ url: encodeURIComponent(fileUrl) }],
    ).pipe(
      catchError(this.handleError<{ contentLength: number | null; contentType: string }>({ contentLength: null, contentType: '' })),
    );
  }

  // ─── Adapter pass-throughs (used by components) ───────────────────────────

  /** Selected tenant observable — delegated to the HTTP adapter */
  get selectedUserTenant$() {
    return this.adapter.selectedUserTenant$;
  }

  /** Upload a document — delegated to the HTTP adapter */
  uploadDocument(file: File, tenantId: string, folder?: string) {
    return this.adapter.uploadDocument(file, tenantId, folder);
  }

  // ─── Managers (derived from supervisors) ─────────────────────────────────

  getManagers(): Observable<Manager[]> {
    return this.getSupervisors().pipe(
      map(supervisors => supervisors.map((s: any) => ({
        id:          s.supervisorEmployeeId ?? s.employeeId ?? s.id,
        name:        s.supervisorName       ?? s.employeeName ?? '',
        designation: s.designation          ?? s.reportingType ?? '',
      } as Manager)))
    );
  }

  // ─── Worked Dates (derived from comp-off requests) ────────────────────────

  getWorkedDates(): Observable<WorkedDate[]> {
    return this.getMyCompOffRequests().pipe(
      map(requests => requests.map(r => ({
        id:            r.id,
        date:          r.dateWorked,
        requestedDate: r.compOffDate ?? null,
        status:        r.status ?? '-',
      } as WorkedDate)))
    );
  }

  // ─── Taken Comp Offs (derived from approved comp-off requests) ───────────

  getTakenCompOffs(): Observable<TakenCompOff[]> {
    return this.getMyCompOffRequests().pipe(
      map(requests =>
        requests
          .filter(r => r.status === 'Approved')
          .map(r => ({
            id:                  r.id,
            workedDateFrom:      r.dateWorked,
            requestedDateFrom:   r.compOffDate,
            status:              r.status,
          } as TakenCompOff))
      )
    );
  }

  // ─── No Backend Endpoint Yet (keep mock) ─────────────────────────────────

  processHourlyRequest(requestId: string, action: 'Approve' | 'Reject', rejectionReason?: string): Observable<any> {
    return this.processLeaveRequest({
      leaveId: requestId,
      action,
      rejectionReason,
    });
  }

  // ─── Gift of Time ─────────────────────────────────────────────────────────

  /** HR/Manager gifts leave days to one or more employees. */
  giftLeave(dto: GiftLeaveDto): Observable<{ success: boolean; message?: string; error?: string }> {
    return this.adapter.post<GiftLeaveDto, { success: boolean; message?: string; error?: string }>(
      'lmGiftLeave', 'GiftLeaveInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  /** Employee avails received gifted leave. */
  availGiftedLeave(dto: AvailGiftLeaveDto): Observable<{ success: boolean; message?: string; error?: string; id?: string }> {
    return this.adapter.post<AvailGiftLeaveDto, { success: boolean; message?: string; error?: string; id?: string }>(
      'lmAvailGiftedLeave', 'AvailGiftLeaveInput', dto
    ).pipe(catchError(this.handleMutationError()));
  }

  /** Get the current employee's gifted leave balance. */
  getMyGiftedLeaveBalance(): Observable<GiftedLeaveBalance> {
    return this.adapter.get<GiftedLeaveBalance>('lmGetMyGiftedLeaveBalance').pipe(
      catchError(this.handleError<GiftedLeaveBalance>({ totalGifted: 0, totalUsed: 0, balanceRemaining: 0, expiresOn: null })),
    );
  }

  /** Get all gifted leave records for the current employee (sent/received). */
  getMyGiftedLeaves(): Observable<any[]> {
    return this.adapter.get<any[]>('lmGetMyGiftedLeaves').pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

  /** HR/Manager: Get all gifted leave records for the tenant. */
  getAllGiftedLeaves(): Observable<any[]> {
    return this.adapter.get<any[]>('lmGetAllGiftedLeaves').pipe(
      catchError(this.handleError<any[]>([])),
    );
  }

}